// src/hooks/useReportMissingSubjects.js
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../supabaseClient";

/** Load all subjects (id, code, name) */
export function useAllSubjects() {
  return useQuery({
    queryKey: ["report-missing-subjects", "subjects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subjects")
        .select("id, code, name")
        .order("code", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Load students with optional filters:
 * - year: "All" or numeric string
 * - paymentType: "All" | "Full" | "Individual"
 *
 * Only Active & not deleted.
 */
export function useStudentsFiltered(year = "All", paymentType = "All") {
  return useQuery({
    queryKey: ["report-missing-subjects", "students", year, paymentType],
    queryFn: async () => {
      let q = supabase
        .from("students")
        .select(
          "id, full_name, contact_no, email, enrollment_year, payment_type, status, is_deleted"
        )
        .eq("status", "Active")
        .eq("is_deleted", false);

      if (year !== "All") {
        q = q.eq("enrollment_year", Number(year));
      }
      if (paymentType !== "All") {
        q = q.eq("payment_type", paymentType);
      }
      const { data, error } = await q.order("full_name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Load enrollments for a given list of student IDs.
 * Only Active & not deleted.
 * Returns rows { student_id, subject_id } (distinct).
 */
// src/hooks/useReportMissingSubjects.js

export function useEnrollmentsByStudents(studentIds = []) {
  return useQuery({
    queryKey: ["report-missing-subjects", "enrollments", studentIds],
    queryFn: async () => {
      if (!studentIds || studentIds.length === 0) return [];

      const { data, error } = await supabase
        .from("enrollments")
        .select("student_id, subject_id, status, is_deleted")
        .in("student_id", studentIds)
        .eq("is_deleted", false); // no status filter here

      if (error) throw error;

      const seen = new Set();
      const rows = [];

      for (const r of data ?? []) {
        // Only treat Active & Completed as "enrolled"
        if (r.status === "Active" || r.status === "Completed") {
          const key = `${r.student_id}|${r.subject_id}`;
          if (!seen.has(key)) {
            seen.add(key);
            rows.push({ student_id: r.student_id, subject_id: r.subject_id });
          }
        }
        // status = 'Withdrawn' or null will be ignored here
        // → subject will be treated as "not enrolled" in buildMissingMap
      }

      return rows;
    },
    enabled: Array.isArray(studentIds) && studentIds.length > 0,
  });
}


/**
 * Load all enrollment years (for dropdown).
 */
export function useAllEnrollmentYears() {
  return useQuery({
    queryKey: ["report-missing-subjects", "all-years"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("students")
        .select("enrollment_year")
        .eq("status", "Active")
        .eq("is_deleted", false);

      if (error) throw error;

      const years = Array.from(
        new Set((data ?? []).map((s) => s.enrollment_year).filter(Boolean))
      ).sort((a, b) => b - a);

      return years.map(String);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Build a Map(student_id => array of subject codes not yet enrolled)
 */
export function buildMissingMap({ students, enrollments, subjects }) {
  const allSubjectIds = subjects.map((s) => s.id);
  const subjectCodeById = new Map(subjects.map((s) => [s.id, s.code]));

  // Build enrollment set per student
  const enrolledByStudent = new Map();
  for (const e of enrollments ?? []) {
    if (!enrolledByStudent.has(e.student_id)) {
      enrolledByStudent.set(e.student_id, new Set());
    }
    enrolledByStudent.get(e.student_id).add(e.subject_id);
  }

  const result = new Map();
  for (const stu of students ?? []) {
    const enrolledSet = enrolledByStudent.get(stu.id) ?? new Set();
    const missingCodes = [];
    for (const sid of allSubjectIds) {
      if (!enrolledSet.has(sid)) {
        const code = subjectCodeById.get(sid);
        if (code) missingCodes.push(code);
      }
    }
    // Sort codes for a stable UI
    missingCodes.sort();
    result.set(stu.id, missingCodes);
  }
  return result;
}
